import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Camera, MapPin, Share2, Play, BadgeCheck, MoreHorizontal, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useIsBelowLg } from '@/hooks/use-mobile';

interface MarketedBy {
  name: string;
  logoUrl?: string | null;
  verified?: boolean;
}

interface ProjectImageGalleryProps {
  images: any[];
  name: string;
  developerLogo?: string | null;
  developerName?: string;
  marketedBy?: MarketedBy | null;
  videoCount?: number;
  onOpenMap?: () => void;
  priceLabel?: string;
  location?: string;
}

export function ProjectImageGallery({
  images,
  name,
  developerLogo,
  developerName,
  marketedBy,
  videoCount = 0,
  onOpenMap,
  priceLabel,
  location,
}: ProjectImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const isMobile = useIsBelowLg();
  const fallback = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop';

  const isVideo = (img: any) => !!img && (img.type === 'video' || img.media_type === 'video');
  const posterOf = (img: any) =>
    img?.thumbnail_url || img?.thumbnailUrl || img?.poster_url || img?.posterUrl || (isVideo(img) ? null : img?.url) || fallback;

  const current = images[selectedImage];
  const currentIsVideo = isVideo(current);
  const mainImg = posterOf(current);
  const photoCount = images.length;
  const derivedVideoCount = images.filter(isVideo).length;
  const totalVideoCount = videoCount || derivedVideoCount;

  // Default the "Marketed By" badge to developer when no agency provided.
  const badge: MarketedBy | null =
    marketedBy ?? (developerName ? { name: developerName, logoUrl: developerLogo, verified: true } : null);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };

  if (isMobile) {
    return (
      <div className="-mx-4 mb-4">
        <div className="relative aspect-[4/3] bg-muted overflow-hidden">
          <img src={mainImg} alt={name} className="w-full h-full object-cover" />
          {currentIsVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="h-14 w-14 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
                <Play className="h-6 w-6 text-white fill-white" />
              </span>
            </div>
          )}

          {badge && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-md pl-2 pr-1.5 py-1 flex items-center gap-1.5 text-[11px] font-medium shadow-sm">
              <span className="text-muted-foreground">Marketed By</span>
              {badge.logoUrl ? (
                <img src={badge.logoUrl} alt={badge.name} className="h-4 w-4 rounded object-cover" />
              ) : (
                <span className="font-semibold">{badge.name}</span>
              )}
              {badge.verified && <BadgeCheck className="h-3.5 w-3.5 text-primary fill-primary/10" />}
            </div>
          )}

          <button
            onClick={handleShare}
            className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full p-1.5 shadow-sm"
          >
            <Share2 className="h-4 w-4 text-foreground" />
          </button>

          {photoCount > 1 && (
            <>
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={() => setSelectedImage(i => (i - 1 + photoCount) % photoCount)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={() => setSelectedImage(i => (i + 1) % photoCount)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-1 rounded-md flex items-center gap-1">
            <Camera className="h-3 w-3" /> {selectedImage + 1}/{photoCount}
          </div>

          {photoCount > 1 && photoCount <= 8 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`h-1.5 rounded-full transition-all ${i === selectedImage ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop: 2-col grid (hero + 1 thumbnail). Pad with hero if fewer images.
  const rightThumbs = [images[1] ?? images[0]];

  return (
    <div className="mb-8">
      <div className="rounded-xl overflow-hidden shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 bg-background lg:h-[552px]">

          {/* Main Image */}
          <div
            className="relative aspect-[16/10] lg:aspect-auto lg:h-full bg-muted group cursor-pointer"
            onClick={() => setOverviewOpen(true)}
          >
            <img src={mainImg} alt={name} className="w-full h-full object-cover" />
            {currentIsVideo && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="h-16 w-16 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center shadow-lg ring-1 ring-white/20">
                  <Play className="h-7 w-7 text-white fill-white" />
                </span>
              </div>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleShare(); }}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-sm hover:bg-white transition-colors"
            >
              <Share2 className="h-4 w-4 text-foreground" />
            </button>

            {badge && (
              <div className="absolute bottom-4 left-4 bg-white rounded-md pl-2.5 pr-2 py-1.5 flex items-center gap-1.5 text-xs font-medium shadow-md">
                {badge.logoUrl ? (
                  <img src={badge.logoUrl} alt={badge.name} className="h-4 w-auto max-w-[80px] object-contain" />
                ) : (
                  <span className="font-semibold text-foreground">{badge.name}</span>
                )}
                {badge.verified && (
                  <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground">
                    <BadgeCheck className="h-3 w-3" />
                  </span>
                )}
                <span className="text-muted-foreground">Marketed By</span>
              </div>
            )}

          </div>

          {/* Right column: 1 thumbnail */}
          <div className="hidden lg:grid grid-rows-1 gap-1 lg:h-full min-h-0">
            {rightThumbs.map((img, i) => {
              const isLast = i === rightThumbs.length - 1;
              const src = posterOf(img);
              const thumbIsVideo = isVideo(img);
              return (
                <button
                  key={img?.id ?? i}
                  onClick={() => { if (img) setSelectedImage(images.indexOf(img)); setOverviewOpen(true); }}
                  className="relative overflow-hidden bg-muted group/thumb h-full w-full min-h-0 p-0 block"
                >
                  <img src={src} alt="" className="w-full h-full object-cover transition-transform group-hover/thumb:scale-[1.02]" />
                  {thumbIsVideo && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="h-11 w-11 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
                        <Play className="h-5 w-5 text-white fill-white" />
                      </span>
                    </div>
                  )}

                  {isLast && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-stretch bg-black/65 backdrop-blur-sm rounded-md text-white text-xs font-medium overflow-hidden divide-x divide-white/20">
                      <span className="flex items-center gap-1 px-2.5 py-1.5 whitespace-nowrap">
                        <Camera className="h-3.5 w-3.5" /> {photoCount}
                      </span>
                      {totalVideoCount > 0 && (
                        <span className="flex items-center gap-1 px-2.5 py-1.5 whitespace-nowrap">
                          <Play className="h-3.5 w-3.5" /> {totalVideoCount}
                        </span>
                      )}
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onOpenMap) onOpenMap();
                          else setOverviewOpen(true);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 whitespace-nowrap hover:bg-white/10 cursor-pointer"
                      >
                        <MapPin className="h-3.5 w-3.5" /> Map
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Overview lightbox — Zameen-style */}
      <Dialog open={overviewOpen} onOpenChange={setOverviewOpen}>
        <DialogContent className="max-w-[min(1280px,95vw)] w-[95vw] h-[92vh] p-0 bg-[#0b0b0b] border-none overflow-hidden flex flex-col gap-0 [&>button]:text-white [&>button]:opacity-80 [&>button:hover]:opacity-100 [&>button]:top-4 [&>button]:right-5 [&>button]:z-20">
          {/* Header */}
          <div className="flex items-center justify-between px-5 lg:px-7 h-14 shrink-0 border-b border-white/10 text-white">
            <div className="min-w-0 pr-8">
              <div className="text-[11px] uppercase tracking-wider text-white/60">
                {currentIsVideo ? 'Video' : 'Photos'}
              </div>
              <h3 className="text-sm font-semibold truncate">{name}</h3>
            </div>
            <div className="text-xs font-medium text-white/80 tabular-nums">
              {selectedImage + 1} <span className="text-white/40">/ {photoCount}</span>
            </div>
          </div>

          {/* Main stage */}
          <div className="relative flex-1 min-h-0 bg-black flex items-center justify-center">
            {currentIsVideo ? (
              <video
                key={current?.id ?? selectedImage}
                src={current?.url}
                poster={posterOf(current)}
                controls
                autoPlay
                playsInline
                className="max-h-full max-w-full"
              />
            ) : (
              <img src={mainImg} alt={name} className="max-h-full max-w-full object-contain" />
            )}
            {photoCount > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition"
                  onClick={() => setSelectedImage(i => (i - 1 + photoCount) % photoCount)}
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition"
                  onClick={() => setSelectedImage(i => (i + 1) % photoCount)}
                  aria-label="Next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          <div className="shrink-0 border-t border-white/10 bg-[#111] px-3 py-3">
            <div className="flex gap-2 overflow-x-auto scroll-smooth [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.3)_transparent]">
              {images.map((img, i) => {
                const stripIsVideo = isVideo(img);
                return (
                  <button
                    key={img.id ?? i}
                    onClick={() => setSelectedImage(i)}
                    className={`relative shrink-0 w-[120px] h-[80px] rounded-md overflow-hidden transition-all ${
                      i === selectedImage
                        ? 'ring-2 ring-primary opacity-100'
                        : 'opacity-55 hover:opacity-90'
                    }`}
                  >
                    <img src={posterOf(img)} alt="" className="w-full h-full object-cover" />
                    {stripIsVideo && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="h-7 w-7 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                          <Play className="h-3.5 w-3.5 text-white fill-white" />
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
